"use strict";
/***************************************************************************
 * Copyright (c) 2018, Voilà contributors                                   *
 * Copyright (c) 2018, QuantStack                                           *
 *                                                                          *
 * Distributed under the terms of the BSD 3-Clause License.                 *
 *                                                                          *
 * The full license is in the file LICENSE, distributed with this software. *
 ****************************************************************************/
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WidgetManager = exports.CONTROL_COMM_TIMEOUT = void 0;
const manager_1 = require("@jupyter-widgets/jupyterlab-manager/lib/manager");
const renderer_1 = require("@jupyter-widgets/jupyterlab-manager/lib/renderer");
const output = __importStar(require("@jupyter-widgets/jupyterlab-manager/lib/output"));
const base = __importStar(require("@jupyter-widgets/base"));
const controls = __importStar(require("@jupyter-widgets/controls"));
// there two imports came 'for free' with webpack 4, from the jupyter-lab-manager plugin
// it seems webpack 5 is better at tree-shaking, so we didn't need to import them explicitly before
require("@jupyter-widgets/base/css/index.css");
require("@jupyter-widgets/controls/css/widgets-base.css");
// Voila imports the following css file, not sure why
// import '@jupyter-widgets/controls/css/widgets.built.css';
const CoreUtils = __importStar(require("@jupyterlab/coreutils"));
const OutputArea = __importStar(require("@jupyterlab/outputarea"));
const LuminoAlgorithm = __importStar(require("@lumino/algorithm"));
const LuminoCommands = __importStar(require("@lumino/commands"));
const LuminoDomutils = __importStar(require("@lumino/domutils"));
const LuminoSignaling = __importStar(require("@lumino/signaling"));
const LuminoVirtualdom = __importStar(require("@lumino/virtualdom"));
const LuminoWidget = __importStar(require("@lumino/widgets"));
const messaging_1 = require("@lumino/messaging");
const loader_1 = require("./loader");
const WIDGET_MIMETYPE = 'application/vnd.jupyter.widget-view+json';
/**
 * Time (in ms) after which we consider the control comm target not responding.
 */
exports.CONTROL_COMM_TIMEOUT = 500;
/**
 * A custom widget manager to render widgets with Voila
 */
class WidgetManager extends manager_1.WidgetManager {
    constructor(context, rendermime, settings) {
        var _a, _b;
        super(context, rendermime, settings);
        rendermime.addFactory({
            safe: false,
            mimeTypes: [WIDGET_MIMETYPE],
            createRenderer: options => new renderer_1.WidgetRenderer(options, this)
        }, 1);
        this._registerWidgets();
        this._loader = loader_1.requireLoader;
        const kernel = (_b = (_a = context.sessionContext) === null || _a === void 0 ? void 0 : _a.session) === null || _b === void 0 ? void 0 : _b.kernel;
        this.connectControlComm();
        if (!kernel) {
            throw new Error('No current kernel');
        }
    }
    connectControlComm() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const commId = base.uuid();
            const kernel = (_b = (_a = this.context.sessionContext) === null || _a === void 0 ? void 0 : _a.session) === null || _b === void 0 ? void 0 : _b.kernel;
            this.controlComm = kernel.createComm('solara.control', commId);
            this.controlCommHandler = {
                onMsg: (msg) => {
                    console.error('No handler');
                },
                onClose: () => {
                    console.error('No handler');
                }
            };
            this.controlComm.onMsg = (msg) => __awaiter(this, void 0, void 0, function* () {
                const data = msg['content']['data'];
                if (data.method === 'reload') {
                    this.controlComm.send({ method: 'reload' });
                }
                else {
                    yield this.controlCommHandler.onMsg(msg);
                }
            });
            this.controlComm.onClose = () => __awaiter(this, void 0, void 0, function* () {
                yield this.controlCommHandler.onClose();
            });
            this.controlComm.open({}, {}, []);
        });
    }
    appStatus() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            // checks if app is still valid (e.g. server restarted and lost the widget state)
            // if we are connected to the same kernel, we'll get a reply instantly
            // however, if we are connected to a new kernel, we rely on the timeout
            // so every time we create a new comm.
            const kernel = (_b = (_a = this.context.sessionContext) === null || _a === void 0 ? void 0 : _a.session) === null || _b === void 0 ? void 0 : _b.kernel;
            const commId = base.uuid();
            const controlComm = kernel.createComm('solara.control', commId);
            controlComm.open({}, {}, []);
            try {
                return yield new Promise((resolve, reject) => {
                    controlComm.onMsg = (msg) => {
                        const data = msg['content']['data'];
                        if (data.method === 'app-status') {
                            resolve({ started: data.started });
                        }
                        else {
                            reject({ ok: false, message: "unexpected message" });
                        }
                    };
                    controlComm.onClose = () => {
                        console.error("closed solara control comm");
                        reject({ ok: false, message: "closed solara control comm" });
                    };
                    setTimeout(() => {
                        reject('timeout');
                    }, exports.CONTROL_COMM_TIMEOUT);
                    controlComm.send({ method: 'app-status' });
                });
            }
            catch (e) {
                return { ok: false, message: e };
            }
        });
    }
    fetchAll() {
        return __awaiter(this, void 0, void 0, function* () {
            // fetch all widgets
            yield this._loadFromKernel();
        });
    }
    /**
     * Dispose all widget models WITHOUT sending comm_close messages.
     *
     * Used on solara's soft-remount after a reconnect lands on a fresh kernel:
     * the old comms belong to a kernel that no longer exists, so closing them
     * over the wire is one wasted message per widget (answered server-side by a
     * benign "No such comm" warning). model.close(true) (comm_closed=true) means
     * "the other side already closed", so the model tears down locally only.
     *
     * Mirrors @jupyter-widgets/base's own clear_state(), but with close(true).
     */
    clearStateLocal() {
        return __awaiter(this, void 0, void 0, function* () {
            // _models is private in @jupyter-widgets/base's ManagerBase, hence the cast.
            const models = yield base.resolvePromisesDict(this._models);
            Object.keys(models).forEach((id) => {
                try {
                    models[id].close(true);
                }
                catch (e) {
                    console.warn('solara clearStateLocal: model.close(true) failed', e);
                }
            });
            this._models = Object.create(null);
        });
    }
    run(appName, args) {
        return __awaiter(this, void 0, void 0, function* () {
            let { path } = args;
            // used for routing
            // should be similar to what we do in navigator.vue
            if (typeof path === 'undefined') {
                // backward compatibility, this was before we used <base>
                path = window.location.href.slice(document.baseURI.length);
            }
            const widget_id_promise = new Promise((resolve, reject) => {
                this.controlCommHandler = {
                    onMsg: (msg) => {
                        const data = msg['content']['data'];
                        if (data.method === 'finished') {
                            resolve(data.widget_id);
                        }
                        else {
                            reject(data.error);
                        }
                    },
                    onClose: () => {
                        console.error("closed solara control comm");
                        reject();
                    }
                };
            });
            this.controlComm.send({ method: 'run', args: Object.assign(Object.assign({}, args), { appName: appName || null }) });
            const widget_id = yield widget_id_promise;
            return widget_id;
        });
    }
    display_view(msg, view, options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (options.el) {
                LuminoWidget.Widget.attach(view.pWidget, options.el);
            }
            if (view.el) {
                view.el.setAttribute('data-voila-jupyter-widget', '');
                view.el.addEventListener('jupyterWidgetResize', (e) => {
                    messaging_1.MessageLoop.postMessage(view.pWidget, LuminoWidget.Widget.ResizeMessage.UnknownSize);
                });
            }
            return view.pWidget;
        });
    }
    loadClass(className, moduleName, moduleVersion) {
        const _super = Object.create(null, {
            loadClass: { get: () => super.loadClass }
        });
        return __awaiter(this, void 0, void 0, function* () {
            if (moduleName === '@jupyter-widgets/base' ||
                moduleName === '@jupyter-widgets/controls' ||
                moduleName === '@jupyter-widgets/output') {
                return _super.loadClass.call(this, className, moduleName, moduleVersion);
            }
            else {
                // TODO: code duplicate from HTMLWidgetManager, consider a refactor
                return this._loader(moduleName, moduleVersion).then(module => {
                    if (module[className]) {
                        return module[className];
                    }
                    else {
                        return Promise.reject('Class ' +
                            className +
                            ' not found in module ' +
                            moduleName +
                            '@' +
                            moduleVersion);
                    }
                });
            }
        });
    }
    restoreWidgets(notebook) {
        return Promise.resolve();
    }
    _registerWidgets() {
        this.register({
            name: '@jupyter-widgets/base',
            version: base.JUPYTER_WIDGETS_VERSION,
            exports: base
        });
        this.register({
            name: '@jupyter-widgets/controls',
            version: controls.JUPYTER_CONTROLS_VERSION,
            exports: controls
        });
        this.register({
            name: '@jupyter-widgets/output',
            version: output.OUTPUT_WIDGET_VERSION,
            exports: output
        });
        // do this not top level, since requirejs might be loaded after this module is loaded
        if (typeof window !== 'undefined' && typeof window.define !== 'undefined') {
            window.define('@jupyter-widgets/base', base);
            window.define('@jupyter-widgets/controls', controls);
            window.define('@jupyter-widgets/output', output);
            window.define('@jupyterlab/coreutils', CoreUtils);
            window.define('@jupyterlab/outputarea', OutputArea);
            window.define('@phosphor/widgets', LuminoWidget);
            window.define('@phosphor/signaling', LuminoSignaling);
            window.define('@phosphor/virtualdom', LuminoVirtualdom);
            window.define('@phosphor/algorithm', LuminoAlgorithm);
            window.define('@phosphor/commands', LuminoCommands);
            window.define('@phosphor/domutils', LuminoDomutils);
            window.define('@lumino/widgets', LuminoWidget);
            window.define('@lumino/signaling', LuminoSignaling);
            window.define('@lumino/virtualdom', LuminoVirtualdom);
            window.define('@lumino/algorithm', LuminoAlgorithm);
            window.define('@lumino/commands', LuminoCommands);
            window.define('@lumino/domutils', LuminoDomutils);
        }
    }
}
exports.WidgetManager = WidgetManager;
//# sourceMappingURL=manager.js.map